import {
  useCallback, useEffect, useId, useRef, useState,
} from 'react';

// Domain
import useFieldSuggestions from '../../../../domain/field-suggestions/useFieldSuggestions';
import { FIELD_SUGGESTIONS_MIN_CHARS } from '../../../../services/field-suggestions/fieldSuggestionsQueries';

// Utils
import cn from '../../../../utils/cn';

// Components
import Input from '../../Input/Input';

// Styles
import styles from './SuggestField.module.css';

/**
 * @typedef {object} SuggestFieldProps
 * @property {string} [value] - Current input value
 * @property {(value: string) => void} onChange - Change handler (string value)
 * @property {(event?: React.FocusEvent<HTMLInputElement>) => void} [onBlur] - Blur handler
 * @property {string} suggestionsUrl - Autocomplete URL from field.text.suggestionsUrl
 * @property {string} [type] - HTML input type (text, email, number)
 * @property {string} [id] - Input id
 * @property {string} [name] - Input name
 * @property {boolean} [hasError] - Whether input has validation error
 * @property {boolean} [isLiveSearch] - Primary border for live search criteria fields
 * @property {boolean} [disabled] - Whether input is disabled
 * @property {boolean} [readOnly] - Read-only (posted, not user-editable)
 * @property {string} [className] - Additional CSS classes
 * @property {string} [placeholder] - Placeholder text
 * @property {React.CSSProperties} [style] - Inline styles (e.g. textAlign)
 * @property {string} [aria-describedby] - Described by ids
 * @property {string | boolean} [aria-invalid] - Invalid state
 */

/**
 * Text input with inline suggestions from an API URL.
 * Free typing is always allowed; selecting a suggestion fills the field.
 * @param {SuggestFieldProps} props
 * @returns {JSX.Element}
 */
function SuggestField({
  value = '',
  onChange,
  onBlur,
  suggestionsUrl,
  type = 'text',
  id,
  name,
  hasError = false,
  isLiveSearch = false,
  disabled = false,
  readOnly = false,
  className = '',
  placeholder = '',
  style,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
}) {
  const listboxId = useId();
  const wrapperRef = useRef(/** @type {HTMLDivElement | null} */ (null));
  /**
   * Fetch only once the user types: a pre-filled value must not open the list
   * when the form opens. After picking a suggestion, skip fetch until the user types again.
   */
  const [fetchEnabled, setFetchEnabled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const inputValue = typeof value === 'string' ? value : '';
  const { suggestions } = useFieldSuggestions({
    suggestionsUrl,
    value: inputValue,
    enabled: fetchEnabled,
  });

  const showList = isOpen && suggestions.length > 0;

  useEffect(() => {
    setActiveIndex(-1);
    if (suggestions.length > 0 && inputValue.length >= FIELD_SUGGESTIONS_MIN_CHARS) {
      setIsOpen(true);
    }
  }, [suggestions, inputValue]);

  useEffect(() => {
    if (!showList) return undefined;

    /**
     * @param {MouseEvent} event
     */
    const handlePointerDown = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(/** @type {Node} */ (event.target))) {
        setIsOpen(false);
        setActiveIndex(-1);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [showList]);

  const selectSuggestion = useCallback((/** @type {string} */ suggestion) => {
    setFetchEnabled(false);
    onChange(suggestion);
    setIsOpen(false);
    setActiveIndex(-1);
  }, [onChange]);

  /**
   * @param {React.ChangeEvent<HTMLInputElement>} event
   */
  const handleChange = (event) => {
    if (readOnly) return;
    setFetchEnabled(true);
    onChange(event.target.value);
    setIsOpen(true);
  };

  /**
   * @param {React.KeyboardEvent<HTMLInputElement>} event
   */
  const handleKeyDown = (event) => {
    if (!showList && event.key !== 'Escape') {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setIsOpen(true);
      setActiveIndex((prev) => {
        if (suggestions.length === 0) return -1;
        return prev < suggestions.length - 1 ? prev + 1 : 0;
      });
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((prev) => {
        if (suggestions.length === 0) return -1;
        return prev > 0 ? prev - 1 : suggestions.length - 1;
      });
      return;
    }

    if (event.key === 'Enter' && activeIndex >= 0 && suggestions[activeIndex]) {
      event.preventDefault();
      selectSuggestion(suggestions[activeIndex]);
      return;
    }

    if (event.key === 'Escape') {
      setIsOpen(false);
      setActiveIndex(-1);
    }
  };

  /**
   * @param {React.FocusEvent<HTMLInputElement>} event
   */
  const handleBlur = (event) => {
    onBlur?.(event);
  };

  const activeOptionId = activeIndex >= 0
    ? `${listboxId}-option-${activeIndex}`
    : undefined;

  return (
    <div
      ref={wrapperRef}
      className={cn([styles.wrapper, className])}
    >
      <Input
        id={id}
        name={name}
        type={type}
        value={inputValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          if (suggestions.length > 0) setIsOpen(true);
        }}
        disabled={disabled}
        readOnly={readOnly}
        hasError={hasError}
        isLiveSearch={isLiveSearch}
        placeholder={placeholder}
        style={style}
        role="combobox"
        autoComplete="off"
        aria-autocomplete="list"
        aria-expanded={showList}
        aria-controls={listboxId}
        aria-activedescendant={activeOptionId}
        aria-describedby={ariaDescribedBy}
        aria-invalid={/** @type {boolean | 'true' | 'false' | 'grammar' | 'spelling' | undefined} */ (
          ariaInvalid
        )}
      />

      {showList && (
        <ul
          id={listboxId}
          className={styles.listbox}
          role="listbox"
        >
          {suggestions.map((suggestion, index) => (
            <li
              key={suggestion}
              id={`${listboxId}-option-${index}`}
              role="option"
              aria-selected={index === activeIndex}
              className={cn([
                styles.option,
                index === activeIndex && styles['option-active'],
              ])}
              onMouseDown={(event) => {
                // Prevent input blur before click applies the value
                event.preventDefault();
                selectSuggestion(suggestion);
              }}
              onMouseEnter={() => setActiveIndex(index)}
            >
              {suggestion}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default SuggestField;
